import { ReactElement, useEffect, useState } from 'react';
import DeviceDetails from './DeviceDetails';
import DevicesActivity from './DevicesActivity';
import FloorView from './FloorView';
import GroupsList from './GroupsList';
import { DeviceAttributesV1 } from '../constants/telosair-interfaces';
import Loader from './loader';
import AddDeviceModal from '../modals/AddDevice';
import AddGroupModal from '../modals/AddGroup';
import { useData } from '../context/data.context';

type DevicesProps = {
  selectedDevices: number[];
  selectedParameters: string[];
  floorViewExpanded: boolean;
  setExpandAll: () => void;
  expandAll: boolean;
};

/**
 * Devices component renders the list of groups and devices installed in it.
 * @param props - Properties of Devices component.
 * @returns - Devices Component.
 */
export default function Devices(props: DevicesProps): ReactElement {
  const { devices, groupedData: groups, refresh, loading } = useData();
  const {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    selectedDevices,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    selectedParameters,
    floorViewExpanded,
    setExpandAll,
    expandAll
  } = props;
  const [selectedGroup, setSelectedGroup] = useState<number | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isGroupModalOpen, setIsGroupModalOpen] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState<DeviceAttributesV1 | null>(null);
  const [selectedFloor, setSelectedFloor] = useState<number | null>(null);

  /**
   * Handle close the Create Group modal.
   * @param newData - Check weather new group is created.
   */
  const handleGroupModalClose = (newData?: boolean): void => {
    if (newData) {
      refresh();
    }
    setIsGroupModalOpen(false);
  };

  /**
   * Function to handle group id change.
   * @param data - Properties of onGroupIdChange function.
   */
  const onGroupIdChange = (data: number): void => {
    setSelectedDevice(null);
    setSelectedGroup(data);
  };

  useEffect(() => {
    if (floorViewExpanded && selectedGroup) {
      setSelectedFloor(selectedGroup);
    } else {
      setSelectedFloor(null);
    }
  }, [selectedGroup, floorViewExpanded]);

  if (loading) {
    return (
      <div className="flex flex-1 relative mt-64">
        <Loader />
      </div>
    );
  }

  const GROUP_DETAILS = selectedGroup
    ? groups.filter((group) => group.groupId === selectedGroup)[0]
    : null;

  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-8 gap-0 md:gap-4 my-4 mx-0">
        <div className="col-span-2 md:col-span-6 md:h-[75vh] overflow-auto">
          {floorViewExpanded && (
            <div className="mb-4 h-1/2 bg-container-500 overflow-y-auto">
              <FloorView
                selectedGroup={selectedGroup}
                selectedParameters={[]}
                onGroupSelection={(group) => {
                  setSelectedGroup(group);
                  setSelectedFloor(group);
                }}
                groups={groups}
                onBack={() => setSelectedGroup(null)}
              />
            </div>
          )}
          <GroupsList
            selectedFloor={selectedFloor}
            selectedGroup={selectedGroup}
            onGroupSelection={onGroupIdChange}
            groups={groups}
            sensors={devices.length}
            expandAll={expandAll}
            setExpandAll={setExpandAll}
            selectedDevice={selectedDevice}
            setSelectedDevice={(device) => {
              setSelectedDevice(device);
              // Find which group this device belongs to and set it as selected
              const deviceGroup = groups.find((group) =>
                group.sensorDetailsList.some(
                  (sensor) => sensor.serial_number === device.serial_number
                )
              );
              setSelectedGroup(deviceGroup ? deviceGroup.groupId : null);
            }}
          />
        </div>
        <div className="grid md:grid-cols-1 grid-cols-2 col-span-2 xl:col-span-2 gap-4 h-fit mt-4 md:mt-0">
          <div className="col-span-1 md:col-span-1 lg:col-span-6 xl:col-span-12">
            <div className="flex">
              <DeviceDetails
                groupDetails={GROUP_DETAILS}
                deviceData={devices}
                selectedDevice={selectedDevice}
              />
            </div>
          </div>
          <div className="col-span-1 md:col-span-1 lg:col-span-6 xl:col-span-12">
            <div className="flex h-full">
              <DevicesActivity />
            </div>
          </div>
        </div>
      </div>
      {isModalOpen && (
        <AddDeviceModal
          groups={groups}
          setIsGroupModalOpen={setIsGroupModalOpen}
          handleClose={() => setIsModalOpen(false)}
        />
      )}
      {/* {Add a new group} */}
      {isGroupModalOpen && <AddGroupModal handleClose={handleGroupModalClose} />}
    </>
  );
}
